const BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiError extends Error {
  status: number;
  body?: any;
}

function getToken(): string | null {
  return localStorage.getItem('jani_access_token');
}

function setToken(token: string) {
  localStorage.setItem('jani_access_token', token);
}

function removeToken() {
  localStorage.removeItem('jani_access_token');
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.accessToken) {
      setToken(data.accessToken);
      return data.accessToken;
    }
  } catch {
    // silently fail
  }
  return null;
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const url = `${BASE}${path}`;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  };

  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const impersonateUserId = localStorage.getItem('jani_impersonate_user_id');
  if (impersonateUserId) headers['X-Impersonate-User-Id'] = impersonateUserId;

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return api(path, options, false);
    }
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  let body: any;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) body = await res.json();
  else body = await res.text();

  if (!res.ok) {
    const err = new Error(body?.error || `HTTP ${res.status}`) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body as T;
}

export async function apiRaw(
  path: string,
  options: RequestInit = {})
: Promise<Response> {
  const url = `${BASE}${path}`;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const impersonateUserId = localStorage.getItem('jani_impersonate_user_id');
  if (impersonateUserId) headers['X-Impersonate-User-Id'] = impersonateUserId;

  return fetch(url, { ...options, headers });
}

export { setToken, removeToken, getToken };
